import React from 'react';
import { X, Scale, Shield, AlertTriangle, Building, Users, CreditCard, FileText } from 'lucide-react';

interface LegalDisclaimerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LegalDisclaimerModal: React.FC<LegalDisclaimerModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-300"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden transform animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 p-6 text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-600/20 to-red-700/20"></div>
        
            <button
              onClick={onClose}
              className="absolute top-1 right-5 p-2 !z-[9999] hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-110"
            >
              <X className="w-6 h-6" />
            </button>
          
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
              <Scale className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">Legal Disclaimer</h2>
              <p className="text-amber-100">SaaS Platform Service Agreement - Effective Date: January 1, 2025</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-8">
          <div className="prose prose-slate max-w-none">
            
            {/* Platform Role */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 mb-8 border border-amber-200">
              <div className="flex items-center gap-3 mb-4">
                <Building className="w-6 h-6 text-amber-600" />
                <h3 className="text-xl font-bold text-amber-900 m-0">Platform Service Provider Role</h3>
              </div>
              <div className="space-y-4">
                <p className="text-amber-800 m-0">
                  QCBT Platform operates exclusively as a Software-as-a-Service (SaaS) technology provider. 
                  We supply examination software infrastructure and do not conduct, authorize, or validate examinations.
                </p>
                <div className="bg-amber-100 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-800 mb-2">What We Provide:</h4>
                  <ul className="text-amber-700 text-sm space-y-1">
                    <li>• Computer-based testing software platform</li>
                    <li>• AI-powered proctoring technology</li>
                    <li>• Secure examination environment</li>
                    <li>• Technical infrastructure and hosting</li>
                    <li>• Platform maintenance and support</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Customer Responsibilities */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">Customer Responsibilities</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2">Examination Authority</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Creating and uploading exam content</li>
                    <li>• Setting examination policies and rules</li>
                    <li>• Determining grading criteria and standards</li>
                    <li>• Validating student eligibility</li>
                    <li>• Managing examination schedules</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2">Result Management</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Publishing examination results</li>
                    <li>• Issuing certificates and credentials</li>
                    <li>• Handling result disputes and appeals</li>
                    <li>• Maintaining academic records</li>
                    <li>• Ensuring compliance with regulations</li>
                  </ul>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h4 className="font-semibold text-red-800 m-0">Critical Disclaimer</h4>
                </div>
                <p className="text-red-700 text-sm m-0">
                  QCBT Platform does not validate, endorse, or guarantee the accuracy, legitimacy, or 
                  academic value of any examination conducted using our software. All examination content, 
                  results, and certifications are the sole responsibility of the Customer.
                </p>
              </div>
            </div>

            {/* Billing Model */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">Credit-Based Billing Model</h3>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h4 className="font-semibold text-green-800 mb-4">Per-Exam Paper, Per-Candidate Charging</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-green-800">Credit Consumption Model</p>
                      <p className="text-sm text-green-700">
                        Each examination session consumes credits based on no of exam paper, exam paper complexity, 
                        duration, proctoring level, and number of students taking the exam.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-green-800">Transparent Pricing</p>
                      <p className="text-sm text-green-700">
                        Credit costs are calculated per exam paper per student. Customers can view 
                        credit consumption in real-time through their dashboard.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-green-800">No Hidden Fees</p>
                      <p className="text-sm text-green-700">
                        All charges are credit-based. No setup fees, monthly minimums, or surprise charges. 
                        Pay only for actual platform usage.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Liability Limitations */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">Liability Limitations</h3>
              </div>
              
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-slate-800">Technology Service Only</h4>
                  <p className="text-slate-600 text-sm">
                    Our liability is limited to providing functional software services. We are not responsible 
                    for examination content, academic decisions, or institutional policies.
                  </p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-slate-800">Customer Indemnification</h4>
                  <p className="text-slate-600 text-sm">
                    Customers indemnify QCBT Platform against claims arising from their examination content, 
                    grading decisions, or institutional policies implemented using our software.
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-slate-800">Academic Disputes</h4>
                  <p className="text-slate-600 text-sm">
                    All academic disputes, grade appeals, and certification challenges must be resolved 
                    directly between students and the examining institution.
                  </p>
                </div>
              </div>
            </div>

            {/* Data and Results */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Data Ownership and Results</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Customer Data Ownership</h4>
                    <p className="text-blue-700 text-sm">
                      All examination content, student data, and results remain the property of the Customer. 
                      We provide secure storage and processing services only.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Result Publication</h4>
                    <p className="text-blue-700 text-sm">
                      Customers have exclusive authority to publish, withhold, or modify examination results. 
                      QCBT Platform cannot and will not interfere with result publication decisions.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Data Export</h4>
                    <p className="text-blue-700 text-sm">
                      Customers can export all their data at any time. We provide standard formats for 
                      easy migration to other systems if needed.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Compliance Responsibility</h4>
                    <p className="text-blue-700 text-sm">
                      Customers are responsible for ensuring their use of our platform complies with 
                      applicable educational regulations and accreditation requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Regulatory Compliance */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Regulatory and Accreditation Notice</h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                  <h4 className="font-semibold text-amber-800 m-0">Important Notice</h4>
                </div>
                <div className="space-y-3 text-sm text-amber-700">
                  <p>
                    QCBT Platform is not an accredited educational institution and does not provide 
                    educational credentials, degrees, or certifications.
                  </p>
                  <p>
                    Customers must ensure their examinations comply with relevant educational standards, 
                    accreditation requirements, and local regulations in their jurisdiction.
                  </p>
                  <p>
                    Any certifications or credentials issued based on examinations conducted through our 
                    platform are the sole responsibility and authority of the Customer institution.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">Legal Contact Information</h3>
              </div>
              <p className="text-slate-700 mb-4">
                For legal inquiries, service agreements, or clarifications about our SaaS model:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="font-medium text-slate-800">Legal Department</p>
                  <p className="text-slate-600">msqubeit@gmail.com</p>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Business Inquiries</p>
                  <p className="text-slate-600">cbt@msqube.com</p>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Compliance Officer</p>
                  <p className="text-slate-600"> baishakhi@qcbt.in</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};